import React, { useMemo } from "react";

import tw, { styled } from "twin.macro";

import { Reviews } from "@components/Reviews/Reviews";
import { Sections } from "@components/Sections/Sections";
import ReviewsInline from "@components/Reviews/ReviewsInline";

import { Title, TitleWrapper } from "./Shared";

import { useSite } from "~/hooks/useSite";

import { useProduct } from "~/templates/product/useProduct";

import {
  Link,
  Badge,
  Price,
  Container,
  Breadcrumbs,
  ProductLayout,
  BuyNowPayLaterWidget,
  ServiceHighlightsTicker,
} from "@bared/ui";

const InlineReviews = styled(ReviewsInline)`
  ${tw`whitespace-nowrap`}
  & .ruk-rating-snippet-count {
    text-decoration: underline;
    text-underline-offset: 0.1rem;
  }
`;

export interface ProductProps {
  siblingColours?: string[];
  children: React.ReactNode;
  swatches?: React.ReactNode;
  imageGallery?: React.ReactNode;
  reviewNugget?: React.ReactNode;
  reviewsSummary?: React.ReactNode;
  productUpsells?: React.ReactNode;
  asSeenOnSection?: React.ReactNode;
  howToStyleSection?: React.ReactNode;
  seoFeatureDescription?: React.ReactNode;
  productRecommendations?: React.ReactNode;
  featureAndShoeCareSheet?: React.ReactNode;
}

export function Product({
  children,
  swatches,
  imageGallery,
  reviewNugget,
  reviewsSummary,
  siblingColours,
  productUpsells,
  asSeenOnSection,
  howToStyleSection,
  seoFeatureDescription,
  productRecommendations,
  featureAndShoeCareSheet,
}: ProductProps) {
  const {
    page,
    price,
    handle,
    template,
    reviewSku,
    reviewGroup,
    isFinalSale,
    productName,
    breadcrumbs,
    productColor,
    productGender,
    compareAtPrice,
  } = useProduct();

  const site = useSite();

  // Sections in the "additionalFlexibleSections" field from the template are
  // displayed for every product.
  // The Sections component expects the sections to be in a page object
  // like { content: ... }, so that's what we are doing here.
  const templateSections = useMemo(
    () =>
      template.additionalFlexibleSections &&
      0 < template.additionalFlexibleSections.length
        ? { content: template.additionalFlexibleSections }
        : null,
    [template],
  );

  // Sections in the "additionalSections" field from the template can be
  // filtered by product tag. They are already filtered on the server.
  // The Sections component expects the sections to be in a page object
  // like { content: ... }, so that's what we are doing here.
  const templateFilteredSections = useMemo(
    () =>
      template.additionalSections && 0 < template.additionalSections.length
        ? { content: template.additionalSections }
        : null,
    [template],
  );

  return (
    <>
      <div className="bg-neutral">
        <Container>
          <div className="space-y-mini">
            <ProductLayout
              imageGallery={imageGallery}
              stickyOffsetClassName="top-[122px]"
            >
              <div className="flex flex-col space-y-mini py-mini">
                {0 < breadcrumbs.length && (
                  <Breadcrumbs>
                    {breadcrumbs.map((breadcrumb) => (
                      <Link
                        variant="link"
                        key={breadcrumb.url}
                        href={breadcrumb.url}
                      >
                        {breadcrumb.title}
                      </Link>
                    ))}
                  </Breadcrumbs>
                )}
                <div className="space-y-micro">
                  <TitleWrapper className="flex items-start justify-between">
                    <div className="flex flex-col">
                      <Title className="mb-micro">{productName}</Title>
                      <Price price={price} compareAtPrice={compareAtPrice} />
                    </div>
                    <div className="flex flex-col items-end gap-micro">
                      {reviewGroup && (
                        <InlineReviews productGroup={reviewGroup} />
                      )}
                      {!!isFinalSale && (
                        <Badge variant="secondary" size="small">
                          Final Sale
                        </Badge>
                      )}
                    </div>
                  </TitleWrapper>
                  {"AU" === site.countryCode && (
                    <BuyNowPayLaterWidget price={price} />
                  )}
                  {!!productColor && (
                    <p className="text-body text-caption leading-body">
                      <span className="font-medium">Colour: </span>
                      {productColor}
                    </p>
                  )}
                  {swatches && <>{swatches}</>}
                </div>
                <div>
                  <div className="mb-mini">{children}</div>

                  {seoFeatureDescription && (
                    <div className="mb-mini">{seoFeatureDescription}</div>
                  )}
                  {featureAndShoeCareSheet && <>{featureAndShoeCareSheet}</>}
                  {reviewNugget && (
                    <div className="mt-midi">{reviewNugget}</div>
                  )}
                  {productUpsells && (
                    <div className="mt-maxi">{productUpsells}</div>
                  )}
                  <ServiceHighlightsTicker
                    className="mt-maxi"
                    freeShippingThreshold={site.freeShippingThreshold}
                  />
                </div>
              </div>
            </ProductLayout>
            {howToStyleSection && <>{howToStyleSection}</>}
            {page?.content && 0 < page.content.length && (
              <div className="-mx-mini md:-mx-maxi">
                <Sections page={page} />
              </div>
            )}
            {asSeenOnSection && <>{asSeenOnSection}</>}
          </div>

          {reviewGroup && reviewSku && (
            <Reviews
              sku={reviewSku}
              productGroup={reviewGroup}
              productHandle={handle}
              gender={productGender}
              siblingColours={siblingColours}
              reviewsSummary={reviewsSummary}
            />
          )}
        </Container>
      </div>

      <Container>
        {productRecommendations && <>{productRecommendations}</>}
      </Container>

      {templateFilteredSections && (
        <div>
          <Sections page={templateFilteredSections} />
        </div>
      )}

      {templateSections && (
        <div>
          <Sections page={templateSections} />
        </div>
      )}
    </>
  );
}
